import { Accordion, AccordionDetails, Typography, useEventCallback } from '@mui/material';
import { memo, useState } from 'react';
import { AccordionSummary } from 'shell/components/shared/summary-accordion';
import { ExpandMore } from '@mui/icons-material';
import { useFormik } from 'formik';
import Form, { AnnullamentVM } from './form';
import { PmiButton } from 'shell/components/shared/button';
import { __PRACTICE_API_URL__ } from 'shell/constants/environment';
import { useHttpClient } from 'shell/hooks/use-http-fetch/stateless';
import { ResponsePromise } from 'shell/hooks/use-http-fetch/stateless/type';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import * as yup from 'yup';

interface AccordionAnnullamentProps {
  codeEnte?: string;
  yearId?: string;
  typeRegister?: string;
  progressive?: string;
  disabled?: boolean;
  noteInitial?: string;
  openInitail?: boolean;
}

const AccordionAnnullamentComponent = ({
  codeEnte,
  yearId,
  typeRegister,
  progressive,
  disabled = false,
  noteInitial = '',
  openInitail = true,
}: AccordionAnnullamentProps): React.ReactElement => {
  const [open, setOpen] = useState(openInitail);

  const navigate = useNavigate();

  const insertData = useHttpClient(
    `${__PRACTICE_API_URL__}/pratiche/annullo-operazione/annullamento-pratica`,
    {
      options: { method: 'POST' },
    }
  );

  const onSubmit = useEventCallback((values: AnnullamentVM) => {
    const data = {
      codiceEnte: codeEnte || undefined,
      tipoRegistro: typeRegister || undefined,
      idAnnoPratica: yearId ? Number.parseInt(yearId, 10) : undefined,
      progressivoPratica: progressive ? Number.parseInt(progressive, 10) : undefined,
      note: values.note || undefined,
    };

    (insertData({ data }) as ResponsePromise<void>).then(() => {
      toast.success('Operazione eseguita con successo');

      navigate('/pratiche/ricerca');
    });
  });

  const formContext = useFormik<AnnullamentVM>({
    initialValues: {
      note: noteInitial,
    },
    validationSchema: yup.object().shape({
      note: yup.string().nullable().required('Il campo è obbligatorio'),
    }),
    onSubmit,
  });

  return (
    <Accordion
      expanded={open}
      onChange={() => {
        setOpen(!open);
      }}
    >
      <AccordionSummary
        aria-controls="accordion-practice-not-available-content"
        id="accordion-practice-not-available-header"
        expandIcon={<ExpandMore />}
      >
        <Typography>Annullamento pratica</Typography>
      </AccordionSummary>
      <AccordionDetails>
        <Form
          onChange={formContext.setFieldValue}
          values={formContext.values}
          errors={formContext.errors}
          touched={formContext.touched}
          disabled={disabled}
        />
        {disabled ? undefined : (
          <div className="pmi-pt-3">
            <PmiButton
              variant="outlined"
              color="cdmBrandThird"
              onClick={() => {
                formContext.handleSubmit();
              }}
              id="confirm"
            >
              Conferma
            </PmiButton>
          </div>
        )}
      </AccordionDetails>
    </Accordion>
  );
};

AccordionAnnullamentComponent.displayName = 'Accordion annullament';

const AccordionAnnullament = memo(AccordionAnnullamentComponent);

export default AccordionAnnullament;
